import { AVAILABLE_SERVICE, CONSULTATION_TYPE } from '@abiglobalhealth/go-types'

import { Button } from '@/components/Button'
import { useGenerateReport } from '@/components/Consultation/steps/Answer/useGenerateReport'
import { PrescriptionIssuedIllustration } from '@/components/icons/PrescriptionIssuedIllustration'
import { PrescriptionRejectedIllustration } from '@/components/icons/PrescriptionRejectedIllustration'
import { ReportReadyIllustration } from '@/components/icons/ReportReadyIllustration'
import { useConsultationType } from '@/hooks/useConsultationType'
import { usePrescription } from '@/hooks/usePrescription'
import { getSlugTranslation } from '@/locale/utils'
import { isPrescription } from '@/utils/consultation'

const COMPLETED_CONSULTATION_TYPE = {
  [CONSULTATION_TYPE.MENTAL]: 'wellnessAssessment',
  [CONSULTATION_TYPE.PRESCRIPTION]: 'prescriptionRequest',
  [CONSULTATION_TYPE.VIDEO]: 'videoConsultation'
} as const

const getCompletedConsultationContent = (consultationType: AVAILABLE_SERVICE, isPrescriptionDeclined?: boolean) => {
  if (isPrescription(consultationType)) {
    if (isPrescriptionDeclined) {
      return {
        Illustration: PrescriptionRejectedIllustration,
        completedTranslationKey: 'general.consultation.prescriptionRequest.prescriptionDeclined'
      }
    }

    return {
      Illustration: PrescriptionIssuedIllustration,
      completedTranslationKey: 'general.consultation.prescriptionRequest.prescriptionIssued'
    }
  }

  const completedConsultationType = COMPLETED_CONSULTATION_TYPE[consultationType as keyof typeof COMPLETED_CONSULTATION_TYPE] ?? consultationType

  return {
    Illustration: ReportReadyIllustration,
    completedTranslationKey: `general.consultation.${completedConsultationType}.consultationCompleted`
  }
}

export const ReportPrompt = () => {
  const { consultationType } = useConsultationType()
  const { prescription } = usePrescription()
  const { Illustration, completedTranslationKey } = getCompletedConsultationContent(consultationType, prescription?.isRefused)
  const generateReport = useGenerateReport()

  return (
    <div className="flex flex-1 flex-col items-center gap-8 overflow-y-auto px-4 py-8 text-center" data-testid="report-prompt">
      <h2 className="w-full text-2xl leading-normal font-bold">{getSlugTranslation(completedTranslationKey)}</h2>

      <Illustration />

      <div className="flex w-full max-w-md flex-col gap-8" data-testid="answer-container">
        <p className="text-base leading-normal font-normal">{getSlugTranslation(`${completedTranslationKey}.description`)}</p>
        <Button customClassName="text-sm leading-6" data-testid="generate-report" hasShadow={false} onClick={generateReport}>
          {getSlugTranslation('general.consultation.getReport')}
        </Button>
      </div>
    </div>
  )
}
